import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  type MutationHook,
  OPERATION_MFA_VIEW_TYPE,
  OperationMFAVerification,
  type OperationMFAVerificationOptions,
  type OperationMFAVerificationRef,
  type OperationMFAViewData,
} from '@/features/operation-mfa-verification';
import { ModalComponentWrapper } from '@/shared/components';
import { useTwoFactorAuthIdentities } from '@/entities/mfa-identity';
import { useTranslation } from 'react-i18next';
import type { AxiosError } from 'axios';
import type { TwoFAParams } from '@/shared/types';

interface UseWithdrawMFAFlowOptions<
  TParams = unknown,
  TResponse = unknown,
  TError extends AxiosError = AxiosError,
> {
  initialParams: TParams;
  injectMFA: (params: TParams, mfa: TwoFAParams) => TParams;
  useMutationHook: MutationHook<TParams, TResponse, TError>;
  onSuccess: (data: TResponse) => void;
  onError: () => void;
  onBack: () => void;
  onClose: () => void;
  onCancel: () => void;
  onMFASetupComplete: () => void;
  modalTitle: string;
  continueMFA?: boolean;
}

export function useWithdrawMFAFlow<
  TParams = unknown,
  TResponse = unknown,
  TError extends AxiosError = AxiosError,
>({
  initialParams,
  injectMFA,
  useMutationHook,
  onSuccess,
  onError,
  onBack,
  onClose,
  onCancel,
  onMFASetupComplete,
  modalTitle,
  continueMFA,
}: UseWithdrawMFAFlowOptions<TParams, TResponse, TError>): {
  showMFA: boolean;
  startMFAVerification: () => void;
  renderMFAView: () => React.ReactElement | null;
} {
  const { t } = useTranslation();
  const { isTwoFactorAuthIdentitiesLoading } = useTwoFactorAuthIdentities();
  const [showMFA, setShowMFA] = useState(false);
  const [currentView, setCurrentView] = useState<OperationMFAViewData>(null);
  const mfaComponentRef = useRef<OperationMFAVerificationRef | null>(null);
  const shouldAutoTriggerMFARef = useRef(false);

  const startMFAVerification = useCallback(() => {
    setShowMFA(true);
    shouldAutoTriggerMFARef.current = false;
    setTimeout(() => {
      mfaComponentRef.current?.startRequest();
    }, 0);
  }, []);

  useEffect(() => {
    if (continueMFA) {
      shouldAutoTriggerMFARef.current = true;
      setShowMFA(true);
    }
  }, [continueMFA]);

  useEffect(() => {
    if (
      showMFA &&
      shouldAutoTriggerMFARef.current &&
      !isTwoFactorAuthIdentitiesLoading
    ) {
      shouldAutoTriggerMFARef.current = false;
      startMFAVerification();
    }
  }, [showMFA, isTwoFactorAuthIdentitiesLoading, startMFAVerification]);

  const hideMFA = useCallback(() => {
    setShowMFA(false);
    shouldAutoTriggerMFARef.current = false;
  }, []);

  const operationMFAVerificationProps = {
    useMutationHook,
    initialParams,
    injectMFA,
    requireMFASetup: true as const,
    onSuccess,
    onError,
    onBack: () => {
      hideMFA();
      onBack();
    },
    onClose: () => {
      hideMFA();
      onClose();
    },
    onCancel: () => {
      hideMFA();
      onCancel();
    },
    onMFASetupComplete,
    onViewChange: (view?: OperationMFAViewData) => {
      setCurrentView(view || null);
    },
  } satisfies OperationMFAVerificationOptions<TParams, TResponse, TError>;

  const handleBack = useCallback(() => {
    if (
      currentView &&
      OPERATION_MFA_VIEW_TYPE.CONFIRMATION === currentView.view
    ) {
      mfaComponentRef?.current?.handleBackToMethodSelection?.();
      return;
    }

    if (showMFA) {
      hideMFA();
    } else {
      onBack();
    }
  }, [currentView, showMFA, hideMFA, onBack]);

  const renderMFAView = (): React.ReactElement | null => {
    if (!showMFA) return null;

    return (
      <ModalComponentWrapper
        title={t(modalTitle)}
        description={currentView?.description || ''}
        onBack={handleBack}>
        <OperationMFAVerification
          {...operationMFAVerificationProps}
          ref={(instance: { startRequest: () => void } | null) => {
            if (instance) {
              mfaComponentRef.current = instance;
            }
          }}
        />
      </ModalComponentWrapper>
    );
  };

  return {
    showMFA,
    startMFAVerification,
    renderMFAView,
  };
}
